import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import TopBar from "@/components/TopBar";
import { Contacts } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import type { ContactAffiliation, ContactCreate, ContactKind, ContactListItem } from "@/api/types";
import { confirmDialog, toast } from "@/lib/feedback";
import { roleVar } from "@/lib/colors";
import { useUIStore } from "@/store/ui";

const KIND_OPTIONS: { value: ContactKind; label: string }[] = [
  { value: "account_contact", label: "Account contact" },
  { value: "broker_contact", label: "Broker" },
];

const AFFILIATION_OPTIONS: { value: ContactAffiliation; label: string }[] = [
  { value: "external", label: "External" },
  { value: "internal", label: "Internal" },
];

const KIND_LABEL: Record<ContactKind, string> = {
  account_contact: "Account contact",
  broker_contact: "Broker",
};

type KindFilter = "all" | ContactKind;

const KIND_FILTERS: { value: KindFilter; label: string }[] = [
  { value: "all", label: "All kinds" },
  { value: "account_contact", label: "Account contacts" },
  { value: "broker_contact", label: "Brokers" },
];

const isTechmellonEmail = (email: string) => email.trim().toLowerCase().endsWith("@techmellon.com");

export default function ContactsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<KindFilter>("all");
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<ContactListItem | null>(null);
  const isAdmin = useAuthStore((s) => s.user)?.role === "admin";

  const contactsQuery = useQuery({
    queryKey: ["contacts", { q, kind }],
    queryFn: () =>
      Contacts.list({
        q: q || undefined,
        kind: kind === "all" ? undefined : kind,
      }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => Contacts.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contacts"] }),
    onError: (e) => toast.err(`Delete failed: ${apiErrorMessage(e)}`),
  });

  const contacts = contactsQuery.data ?? [];

  return (
    <>
      <TopBar
        title="Contacts"
        onSearch={setQ}
        searchPlaceholder="Filter contacts…"
        actions={
          isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowNew(true)}>
              <Plus className="w-3.5 h-3.5" /> New contact
            </button>
          )
        }
      />

      <div className="px-4 py-4 md:px-6 md:py-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <select
            className="input max-w-[200px]"
            value={kind}
            onChange={(e) => setKind(e.target.value as KindFilter)}
            aria-label="Filter by kind"
          >
            {KIND_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-fg/[0.02] border-b border-fg/[0.05]">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                  Name
                </th>
                <th className="hidden md:table-cell px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                  Title
                </th>
                <th className="hidden md:table-cell px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                  Email
                </th>
                <th className="hidden md:table-cell px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                  Phone
                </th>
                <th className="hidden md:table-cell px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                  Kind
                </th>
                <th className="hidden md:table-cell px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-mut-300">
                  Linked deals
                </th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} className="border-t border-fg/[0.04] hover:bg-fg/[0.02]">
                  <td className="px-3 py-2.5 font-medium">
                    {c.name}
                    <ContactSummary contact={c} />
                  </td>
                  <td className="hidden md:table-cell px-3 py-2.5 text-mut-300">
                    {c.title || "—"}
                  </td>
                  <td className="hidden md:table-cell px-3 py-2.5 text-mut-300">
                    {c.email || "—"}
                  </td>
                  <td className="hidden md:table-cell px-3 py-2.5 text-mut-300">
                    {c.phone || "—"}
                  </td>
                  <td className="hidden md:table-cell px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-mut-300">{KIND_LABEL[c.kind]}</span>
                      <AffiliationBadge affiliation={c.affiliation} />
                    </div>
                  </td>
                  <td className="hidden md:table-cell px-3 py-2.5">
                    <LinkedDeals deals={c.linked_deals} />
                  </td>
                  <td className="px-3 py-2.5 text-right align-top md:align-middle whitespace-nowrap">
                    {c.can_edit && (
                      <>
                        <button
                          className="btn btn-ghost"
                          title="Edit contact"
                          aria-label={`Edit ${c.name}`}
                          onClick={() => setEditing(c)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          className="btn btn-ghost text-accent-pink"
                          title="Delete contact"
                          aria-label={`Delete ${c.name}`}
                          onClick={async () => {
                            if (
                              await confirmDialog({
                                title: `Delete ${c.name}?`,
                                body: "This removes them from every deal they're linked to.",
                                confirmLabel: "Delete contact",
                                danger: true,
                              })
                            ) {
                              removeMutation.mutate(c.id);
                            }
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-10 text-center text-mut-300">
                    {contactsQuery.isLoading ? "Loading…" : "No contacts match."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <ContactModal mode="create" onClose={() => setShowNew(false)} />}
      {editing && <ContactModal mode="edit" contact={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

/** Phones show one column: everything else about the contact stacks under the name. */
function ContactSummary({ contact: c }: { contact: ContactListItem }) {
  return (
    <div className="md:hidden mt-1 space-y-1 font-normal">
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-mut-500">
        {c.title && <span>{c.title} ·</span>}
        <span>{KIND_LABEL[c.kind]}</span>
        <AffiliationBadge affiliation={c.affiliation} />
      </div>
      {(c.email || c.phone) && (
        <div className="flex flex-wrap gap-x-3 text-xs">
          {c.email && (
            <a className="text-accent-azure break-all" href={`mailto:${c.email}`}>
              {c.email}
            </a>
          )}
          {c.phone && (
            <a className="text-accent-azure" href={`tel:${c.phone}`}>
              {c.phone}
            </a>
          )}
        </div>
      )}
      {c.linked_deals.length > 0 && <LinkedDeals deals={c.linked_deals} />}
    </div>
  );
}

function AffiliationBadge({ affiliation }: { affiliation: ContactAffiliation }) {
  const internal = affiliation === "internal";
  return internal ? (
    <span className="chip-tint" style={roleVar("green")}>
      Internal
    </span>
  ) : (
    <span className="chip bg-fg/[0.05] text-mut-300">External</span>
  );
}

function LinkedDeals({ deals }: { deals: { id: string; lead: string }[] }) {
  const openDeal = useUIStore((s) => s.openDeal);
  if (deals.length === 0) return <span className="text-mut-500">—</span>;
  const shown = deals.slice(0, 3);
  const extra = deals.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((d) => (
        <button
          key={d.id}
          onClick={() => openDeal(d.id)}
          className="text-sm text-accent-azure hover:underline truncate max-w-[160px]"
          title={d.lead}
        >
          {d.lead}
        </button>
      ))}
      {extra > 0 && <span className="text-xs text-mut-500">+{extra} more</span>}
    </div>
  );
}

interface ContactModalProps {
  onClose: () => void;
  mode: "create" | "edit";
  contact?: ContactListItem;
}

function ContactModal({ onClose, mode, contact }: ContactModalProps) {
  const qc = useQueryClient();
  const [name, setName] = useState(contact?.name ?? "");
  const [email, setEmail] = useState(contact?.email ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? "");
  const [title, setTitle] = useState(contact?.title ?? "");
  const [kind, setKind] = useState<ContactKind>(contact?.kind ?? "account_contact");
  const [affiliation, setAffiliation] = useState<ContactAffiliation>(
    contact?.affiliation ?? "external",
  );
  const [affiliationTouched, setAffiliationTouched] = useState(false);
  const [notes, setNotes] = useState(contact?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const onEmailChange = (value: string) => {
    setEmail(value);
    setError(null);
    // Auto-suggest internal for techmellon.com addresses, unless the affiliation
    // has been manually overridden.
    if (!affiliationTouched && isTechmellonEmail(value)) {
      setAffiliation("internal");
    }
  };

  const buildPayload = (): ContactCreate => ({
    name: name.trim(),
    email: email.trim() || null,
    phone: phone.trim() || null,
    title: title.trim() || null,
    kind,
    affiliation,
    notes: notes.trim() || null,
  });

  const mutation = useMutation({
    mutationFn: () =>
      mode === "edit" && contact
        ? Contacts.update(contact.id, buildPayload())
        : Contacts.create(buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      onClose();
    },
    onError: (e) => setError(apiErrorMessage(e)),
  });

  return (
    <>
      <div className="overlay-backdrop z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(520px,92vw)] panel p-5 max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {mode === "edit" ? "Edit contact" : "New contact"}
          </h2>
          <button className="btn btn-ghost" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label">Name*</label>
            <input
              className="input"
              autoFocus
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              aria-label="Contact name"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Phone</label>
              <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label">Title</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Kind</label>
              <select
                className="input"
                value={kind}
                onChange={(e) => setKind(e.target.value as ContactKind)}
              >
                {KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Affiliation</label>
              <select
                className="input"
                value={affiliation}
                onChange={(e) => {
                  setAffiliationTouched(true);
                  setAffiliation(e.target.value as ContactAffiliation);
                }}
              >
                {AFFILIATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea
              className="input min-h-[80px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {error && <div className="text-sm text-accent-pink">{error}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={!name.trim() || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending
                ? mode === "edit"
                  ? "Saving…"
                  : "Creating…"
                : mode === "edit"
                  ? "Save changes"
                  : "Create contact"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
